import { useEffect, useMemo, useState } from 'react'
import { Box, Paper, Stack, Table, TableContainer } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import { RootState } from 'store'
import { selectOrderId } from 'store/trips/trips.slice'
import { TripPayload } from 'store/trips/trips.types'
import { EquipmentResponse } from 'store/equipments/equipments.types'
import { useListEquipmentsQuery } from 'store/equipments/equipments.api'
import {
  useListActiveOrdersQuery,
  useListNewOrdersQuery,
} from 'store/orders/orders.api'
import { Order } from 'store/orders/orders.types'
import { setOrders } from 'store/orders/orders.slice'
import DropFormDialog from 'components/drop-form-dialog/drop-form-dialog.component'
import HookFormDialog from 'components/hook-form-dialog/hook-form-dialog.component'

import TripDetailsTableHeaders from './trip-details-table-header/trip-details-table-header'
import TripDetailsTableBody from './trip-details-table-body/trip-details-table-body'
import {
  getDeliveryTasksMissingPickup,
  getOrdersMissingDelivery,
} from './trip-details-table.utils'
import TripBuilderAddButton from '../../trip-builder-selection/trip-builder-add-button'
import TripBuilderSelectionPanel from '../../trip-builder-selection/trip-builder-selection-panel'
import TripDriverSection from '../../trip-builder-selection/trip-driver-section'
import { TripBuilderSelectionMode } from '../../trip-builder-selection/trip-builder-selection.types'
import { useTripFormVariant } from '../../create-trip-context'

interface TripDetailsTableProps {
  tripTasks?: TripPayload[]
  isLandingPage?: boolean
  onTaskStatusUpdated?: () => void
}

const TripDetailsTable = ({
  tripTasks,
  isLandingPage = false,
  onTaskStatusUpdated,
}: TripDetailsTableProps) => {
  const [dropAddressOpen, setDropAddressOpen] = useState(false)
  const [selectionMode, setSelectionMode] =
    useState<TripBuilderSelectionMode>(null)
  const [replaceEquipment, setReplaceEquipment] = useState(false)
  const [replaceTrailerId, setReplaceTrailerId] = useState<string | null>(null)
  const [targetOrderTrailerId, setTargetOrderTrailerId] = useState<
    string | null
  >(null)
  const [isEditingOrders, setIsEditingOrders] = useState(false)
  const [hookDeliveryTask, setHookDeliveryTask] = useState<TripPayload | null>(
    null
  )
  const dispatch = useDispatch()
  const variant = useTripFormVariant()
  const isDriverBuilder = variant === 'driver' && !isLandingPage

  const { newOrders, activerOrders } = useSelector(
    (state: RootState) => state.orders
  )

  const { data: equipmentsData, refetch: refetchEquipments } =
    useListEquipmentsQuery(undefined, { skip: !isDriverBuilder })
  const { data: newOrdersData } = useListNewOrdersQuery(
    { page: 0, size: 100 },
    { skip: !isDriverBuilder }
  )
  const { data: assignedOrdersData } = useListActiveOrdersQuery(
    { page: 0, size: 100 },
    { skip: !isDriverBuilder }
  )

  const equipments: EquipmentResponse[] = equipmentsData ?? []

  const tripOrders = useMemo(() => {
    const merged = new Map<number, Order>()
    for (const order of newOrdersData?.orders ?? []) {
      merged.set(order.orderId, order)
    }
    for (const order of assignedOrdersData?.orders ?? []) {
      merged.set(order.orderId, order)
    }
    return Array.from(merged.values())
  }, [newOrdersData?.orders, assignedOrdersData?.orders])

  useEffect(() => {
    if (!isDriverBuilder) return
    refetchEquipments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDriverBuilder])

  useEffect(() => {
    if (isDriverBuilder && tripOrders.length > 0) {
      dispatch(setOrders({ orders: tripOrders, type: 'ASSIGNED' }))
    }
  }, [dispatch, isDriverBuilder, tripOrders])

  const ordersMissingDelivery = useMemo(
    () => (tripTasks?.length ? getOrdersMissingDelivery(tripTasks) : []),
    [tripTasks]
  )

  const hasDropTrailer = useMemo(
    () => tripTasks?.some((task) => task.type === 'DROP_TRAILER') ?? false,
    [tripTasks]
  )

  const hasTractor = useMemo(
    () => tripTasks?.some((task) => task.type === 'HOOK_TRACTOR') ?? false,
    [tripTasks]
  )

  const hasEquipmentTrailer = useMemo(
    () =>
      tripTasks?.some(
        (task) =>
          (task.type === 'HOOK_TRAILER' || task.type === 'DROP_TRAILER') &&
          task.orderEventId == null &&
          task.sourceOrderPkId == null &&
          !task.orderId
      ) ?? false,
    [tripTasks]
  )

  const trailerHasOrders = (trailerId: string | null) =>
    (tripTasks ?? []).some(
      (task) =>
        (trailerId == null || task.trailerId === trailerId) &&
        (task.type === 'PICKUP' ||
          task.type === 'DELIVERY' ||
          (task.type === 'HOOK_TRAILER' &&
            (task.orderEventId != null || task.sourceOrderPkId != null)) ||
          (task.type === 'DROP_TRAILER' &&
            (task.orderEventId != null || task.sourceOrderPkId != null)))
    )

  const hasPickupOrDelivery = useMemo(
    () =>
      tripTasks?.some(
        (task) =>
          task.type === 'PICKUP' ||
          task.type === 'DELIVERY' ||
          (task.type === 'HOOK_TRAILER' &&
            (task.orderEventId != null || task.sourceOrderPkId != null)) ||
          (task.type === 'DROP_TRAILER' &&
            (task.orderEventId != null || task.sourceOrderPkId != null))
      ) ?? false,
    [tripTasks]
  )

  const deliveryOnlyTask = useMemo(() => {
    if (!tripTasks) return null
    return getDeliveryTasksMissingPickup(tripTasks)[0] ?? null
  }, [tripTasks])

  const hookOrder = useMemo(() => {
    if (!hookDeliveryTask) return undefined
    return tripOrders.find(
      (order) =>
        order.orderId === hookDeliveryTask.sourceOrderPkId ||
        order.userOrderId === hookDeliveryTask.orderId
    )
  }, [hookDeliveryTask, tripOrders])

  const handleEnterDropAddress = (orderKey: string) => {
    const userOrderId = orderKey.startsWith('event:') ? undefined : orderKey
    const orderEventId = orderKey.startsWith('event:')
      ? Number(orderKey.replace('event:', ''))
      : undefined

    const order = userOrderId
      ? [...(newOrders ?? []), ...(activerOrders ?? [])].find(
          (o) => o.userOrderId === userOrderId
        )
      : [...(newOrders ?? []), ...(activerOrders ?? [])].find(
          (o) =>
            o.orderEvents?.some((e) => e.orderEventId === orderEventId) ||
            o.exceptions?.some((e) => e.orderEventId === orderEventId)
        )

    if (order) {
      dispatch(selectOrderId({ orderId: order.orderId }))
    }

    setDropAddressOpen(true)
  }

  const openSelection = (
    mode: Exclude<TripBuilderSelectionMode, null>,
    replace = false,
    editingOrders = false,
    trailerId: string | null = null
  ) => {
    setReplaceEquipment(replace)
    setReplaceTrailerId(mode === 'trailer' ? trailerId : null)
    setTargetOrderTrailerId(mode === 'order' ? trailerId : null)
    setIsEditingOrders(editingOrders)
    setSelectionMode(mode)
  }

  const closeSelection = () => {
    setSelectionMode(null)
    setReplaceEquipment(false)
    setReplaceTrailerId(null)
    setTargetOrderTrailerId(null)
    setIsEditingOrders(false)
  }

  const emptyStateButtons = (
    <Stack spacing={1.5} sx={{ p: 2 }}>
      <TripDriverSection />
      <TripBuilderAddButton
        label="+ Add Tractor"
        onClick={() => openSelection('tractor')}
      />
      <TripBuilderAddButton
        label="+ Add Trailer"
        onClick={() => openSelection('trailer')}
      />
      <TripBuilderAddButton
        label="+ Add Pickup & Delivery"
        onClick={() => openSelection('order')}
      />
    </Stack>
  )

  const afterHookTractorButtons =
    hasTractor && !hasEquipmentTrailer ? (
      <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
        <TripBuilderAddButton
          label="+ Add Trailer"
          onClick={() => openSelection('trailer')}
        />
        {!hasPickupOrDelivery && (
          <TripBuilderAddButton
            label="+ Add Pickup & Delivery"
            onClick={() => openSelection('order')}
          />
        )}
      </Stack>
    ) : null

  const beforeTripButtons =
    !hasTractor && (hasEquipmentTrailer || hasPickupOrDelivery) ? (
      <>
        <TripBuilderAddButton
          label="+ Add Tractor"
          onClick={() => openSelection('tractor')}
        />
        {!hasEquipmentTrailer && (
          <TripBuilderAddButton
            label="+ Add Trailer"
            onClick={() => openSelection('trailer')}
          />
        )}
      </>
    ) : null

  const driverBeforeTripSlot = isDriverBuilder ? (
    <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
      <TripDriverSection />
      {beforeTripButtons}
    </Stack>
  ) : null

  const afterHookTrailerButtons = (trailerId: string | null) =>
    hasEquipmentTrailer && !trailerHasOrders(trailerId) ? (
      <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
        <TripBuilderAddButton
          label="+ Add Pickup & Delivery"
          onClick={() => openSelection('order', false, false, trailerId)}
        />
      </Stack>
    ) : null

  const beforeDeliveryButtons = deliveryOnlyTask ? (
    <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
      <TripBuilderAddButton
        label="+ Enter Hook Trailer Address"
        onClick={() => setHookDeliveryTask(deliveryOnlyTask)}
      />
    </Stack>
  ) : null

  const beforeDropTrailerButtons = (trailerId: string | null) => {
    if (!trailerHasOrders(trailerId)) return null

    return (
      <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
        {ordersMissingDelivery.length > 0 && (
          <TripBuilderAddButton
            label={
              hasDropTrailer
                ? '+ Edit Drop Trailer Address'
                : '+ Enter Drop Trailer Address'
            }
            onClick={() => handleEnterDropAddress(ordersMissingDelivery[0])}
          />
        )}
        <TripBuilderAddButton
          label="+ Add Another Pickup & Delivery"
          onClick={() => openSelection('order', false, false, trailerId)}
        />
      </Stack>
    )
  }

  const afterDropTrailerButtons = hasEquipmentTrailer ? (
    <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
      <TripBuilderAddButton
        label="+ Add Another Trailer"
        onClick={() => openSelection('trailer')}
      />
    </Stack>
  ) : null

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 'none' }}>
        <Table stickyHeader>
          {(!isDriverBuilder || (!!tripTasks && tripTasks.length > 0)) && (
            <TripDetailsTableHeaders
              isLandingPage={isLandingPage}
              showRowActionsColumn={isDriverBuilder}
            />
          )}
          {!!tripTasks && tripTasks.length > 0 && (
            <TripDetailsTableBody
              tripTasks={tripTasks}
              isLandingPage={isLandingPage}
              isDriverBuilder={isDriverBuilder}
              onTaskStatusUpdated={onTaskStatusUpdated}
              onOpenDropAddress={() => setDropAddressOpen(true)}
              onChangeTractor={() => openSelection('tractor', true)}
              onChangeTrailer={(trailerId) =>
                openSelection('trailer', true, false, trailerId)
              }
              onChangePickup={(trailerId) =>
                openSelection('order', false, true, trailerId)
              }
              beforeTripSlot={driverBeforeTripSlot}
              afterHookTractorSlot={
                isDriverBuilder ? afterHookTractorButtons : null
              }
              afterHookTrailerSlot={
                isDriverBuilder ? afterHookTrailerButtons : undefined
              }
              beforeDeliverySlot={
                isDriverBuilder ? beforeDeliveryButtons : null
              }
              beforeDeliveryOrderEventId={deliveryOnlyTask?.orderEventId}
              beforeDropTrailerSlot={
                isDriverBuilder ? beforeDropTrailerButtons : undefined
              }
              afterDropTrailerSlot={
                isDriverBuilder ? afterDropTrailerButtons : null
              }
            />
          )}
        </Table>
      </TableContainer>

      {isDriverBuilder && (!tripTasks || tripTasks.length === 0) && emptyStateButtons}

      {!isLandingPage && !isDriverBuilder && ordersMissingDelivery.length > 0 && (
        <Box p={2}>
          <TripBuilderAddButton
            label={
              hasDropTrailer ? 'Edit Drop Address' : '+ Enter Drop Address'
            }
            onClick={() => handleEnterDropAddress(ordersMissingDelivery[0])}
          />
        </Box>
      )}

      <DropFormDialog
        open={dropAddressOpen}
        handleClose={() => setDropAddressOpen(false)}
      />

      <HookFormDialog
        open={hookDeliveryTask != null}
        orderId={
          hookOrder?.orderId ?? hookDeliveryTask?.sourceOrderPkId ?? null
        }
        userOrderId={hookOrder?.userOrderId ?? hookDeliveryTask?.orderId ?? ''}
        deliveryOrderEventId={hookDeliveryTask?.orderEventId ?? null}
        handleClose={() => setHookDeliveryTask(null)}
      />

      {isDriverBuilder && (
        <TripBuilderSelectionPanel
          open={selectionMode != null}
          mode={selectionMode}
          equipments={equipments}
          orders={tripOrders}
          replaceEquipment={replaceEquipment}
          replaceTrailerId={replaceTrailerId}
          targetOrderTrailerId={targetOrderTrailerId}
          isEditingOrders={isEditingOrders}
          onClose={closeSelection}
        />
      )}
    </>
  )
}

export default TripDetailsTable
